import { Check } from 'lucide-react';
import * as React from 'react';

import type { BrandIconId } from '../../assets/brandIcons';
import { ConnectedServiceIcon } from '../../atoms/ConnectedServiceIcon';
import { cn } from '../../lib/utils';

import type { ConnectDataSourceId } from './ConnectDataStep';
import { WizardPanel, WizardSectionLabel, type WizardPanelProps } from './WizardPanel';

/** Each connect-data source's identity-provider face — same ids, IdP branding. */
const IDP_PROVIDERS: Record<ConnectDataSourceId, { brand: BrandIconId; name: string }> = {
  microsoft: { brand: 'azure', name: 'Microsoft Entra ID' },
  google: { brand: 'google-workspace', name: 'Google Workspace' },
};

const IdpTile = React.forwardRef<
  HTMLButtonElement,
  {
    brand: BrandIconId;
    name: string;
    selected: boolean;
    onSelect: () => void;
    onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
  }
>(function IdpTile({ brand, name, selected, onSelect, onKeyDown }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      role="radio"
      aria-checked={selected}
      // Roving tabindex: only the selected radio is in the tab order; arrow
      // keys move between the rest (WAI-ARIA radiogroup pattern).
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cn(
        'flex flex-1 items-center gap-3 rounded-[11px] border bg-dawn-white px-4 py-3.5 text-left transition-all',
        selected
          ? 'border-dawn-orange ring-[3px] ring-orange-100'
          : 'border-grey-200 hover:border-orange-400 hover:shadow-[0_3px_12px_rgba(0,0,0,0.06)]'
      )}
    >
      <ConnectedServiceIcon service={brand} size={30} />
      <div className="min-w-0">
        <div className="text-[9.5px] font-semibold uppercase tracking-[0.6px] text-grey-400">
          Identity provider
        </div>
        <div className="text-h4 text-dawn-black">{name}</div>
      </div>
      <span
        className={cn(
          'ml-auto flex shrink-0 items-center gap-1 text-[10.5px] font-semibold',
          selected ? 'text-dawn-orange' : 'text-grey-400'
        )}
      >
        {selected ? (
          <>
            Default <Check className="h-3 w-3" strokeWidth={3} />
          </>
        ) : (
          'Set as default'
        )}
      </span>
    </button>
  );
});

export interface DefaultIdpStepProps extends Pick<WizardPanelProps, 'onBack' | 'frameless'> {
  /** Providers connected on the connect-data step, in display order. */
  providers: ConnectDataSourceId[];
  /** Pre-selected default (resume); falls back to the first provider. */
  defaultProvider?: ConnectDataSourceId;
  onContinue: (provider: ConnectDataSourceId) => void;
}

/**
 * Step 3 — choose the DEFAULT identity provider among the sources already
 * connected on the connect-data step (the connection itself happened there).
 * Single-select radio tiles, one always selected, so Continue is always valid
 * and the step has no Skip.
 */
export function DefaultIdpStep({
  providers,
  defaultProvider,
  onBack,
  onContinue,
  frameless,
}: DefaultIdpStepProps) {
  // Callers pass at least one provider (the step only exists after connect-data).
  const first: ConnectDataSourceId = providers[0] ?? 'google';
  const [selected, setSelected] = React.useState<ConnectDataSourceId>(
    defaultProvider && providers.includes(defaultProvider) ? defaultProvider : first
  );

  const single = providers.length === 1;

  const tileRefs = React.useRef<Map<ConnectDataSourceId, HTMLButtonElement>>(new Map());

  // Arrow keys move selection + focus between radios, wrapping at the ends
  // (WAI-ARIA radiogroup pattern). Space/Enter select via the native button.
  const handleKeyDown =
    (id: ConnectDataSourceId): React.KeyboardEventHandler<HTMLButtonElement> =>
    (event) => {
      const delta =
        event.key === 'ArrowDown' || event.key === 'ArrowRight'
          ? 1
          : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
            ? -1
            : 0;
      if (delta === 0) return;
      event.preventDefault();
      const index = providers.indexOf(id);
      const next = providers[(index + delta + providers.length) % providers.length] ?? id;
      setSelected(next);
      tileRefs.current.get(next)?.focus();
    };

  return (
    <WizardPanel
      frameless={frameless}
      title="Choose your default identity provider"
      subtitle={
        single ? (
          <>
            You connected <b>{IDP_PROVIDERS[first].name}</b> in the previous step — it will be your
            default identity provider.
          </>
        ) : (
          <>
            You connected these in the previous step — pick which one Dawn treats as your{' '}
            <b>primary source of identity</b>.
          </>
        )
      }
      onBack={onBack}
      onContinue={() => onContinue(selected)}
    >
      <div className="mt-6" role="radiogroup" aria-label="Default identity provider">
        <WizardSectionLabel>Connected identity providers</WizardSectionLabel>
        <div className="flex flex-col gap-2.5">
          {providers.map((id) => (
            <IdpTile
              key={id}
              ref={(el) => {
                if (el) tileRefs.current.set(id, el);
                else tileRefs.current.delete(id);
              }}
              brand={IDP_PROVIDERS[id].brand}
              name={IDP_PROVIDERS[id].name}
              selected={selected === id}
              onSelect={() => setSelected(id)}
              onKeyDown={handleKeyDown(id)}
            />
          ))}
        </div>
        <p className="mt-4 text-p2 text-grey-400">
          {single
            ? 'Connect another source later to change your default.'
            : 'You can change your default anytime later.'}
        </p>
      </div>
    </WizardPanel>
  );
}
